/**
 * ActivityRow — one row of the dashboard home surface's "recent
 * activity" list (pirx-3apc9). Renders an audit's document name, the
 * localized creation timestamp, and either the risk tag (for completed
 * audits with a known risk level) or the audit-status badge.
 *
 * Extracted from `pages/Dashboard.tsx` (pirx-ojst0.12) — the original
 * file declared ActivityRow inline alongside the Dashboard route
 * component. Dashboard.tsx stays at `pages/` per spec; this row only
 * renders inside the dashboard surface and lives under
 * `features/dashboard/components/`.
 */

import { FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { AuditListItemExtended } from "@/types";
import { RiskTag } from "@/components/shared/RiskTag";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { auditRiskTagLevel } from "@/features/dashboard/dashboard-metrics";

interface ActivityRowProps {
  audit: AuditListItemExtended;
  onClick: () => void;
}

export function ActivityRow({ audit, onClick }: ActivityRowProps) {
  const { i18n } = useTranslation();
  const created = new Date(audit.createdAt);
  const riskLevel = auditRiskTagLevel(audit.overallRiskLevel);
  const clickable = audit.status === "COMPLETED";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className="flex w-full items-center gap-3.5 border-b border-[var(--line)] px-2 py-3 text-left transition-colors last:border-b-0 enabled:hover:bg-[var(--surface-2)] disabled:cursor-default"
      data-test="dashboard-activity-row"
      data-audit-id={audit.id}
    >
      <div className="flex size-[34px] shrink-0 items-center justify-center rounded-[var(--r-sm)] bg-[var(--surface-3)] text-[var(--ink-3)]">
        <FileText className="size-[17px]" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] font-semibold text-[var(--ink)]">
          {audit.documentName}
        </div>
        <div className="mt-0.5 text-xs text-[var(--ink-3)]">
          {created.toLocaleString(i18n.language, {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>
      {audit.status === "COMPLETED" && riskLevel ? (
        <RiskTag level={riskLevel} size="sm" />
      ) : (
        <StatusBadge status={audit.status} errorMessage={audit.errorMessage} />
      )}
    </button>
  );
}
